/**
 * The player's tug.
 *
 * Propulsion is a single azimuthing thruster at the stern: thrust acts at the
 * propeller and points where the rudder points, so at zero speed the wash still
 * swings the stern — a tug can spin on the spot, and this is why.
 */

import { Group, Mesh, Texture, Vector3 } from 'three/webgpu'
import { HULL_WATER_PROFILES, type HullWaterSource } from '../world/HullWaterMasks.ts'
import { Vessel } from '../physics/Vessel.ts'
import { buildTugHull, type HullAttachments } from './hulls.ts'
import { loadTugModel } from './tugModel.ts'
import { LandingRamp } from './LandingRamp.ts'
import { loadIcebreakerModel } from './icebreakerModel.ts'
import { loadCruiseShipModel } from './cruiseShipModel.ts'
import { isLargeShip, loadLargeShipModel, LARGE_SHIP_CONFIG, LARGE_SHIP_PROPULSION, type LargeShipKind } from './largeShipModel.ts'
import { isSmallCraft, loadSmallCraftModel, SMALL_CRAFT_CONFIG, SMALL_CRAFT_PROPULSION, type SmallCraftKind } from './smallCraftModel.ts'

export type VesselKind = 'tug' | 'icebreaker' | 'cruise-ship' | SmallCraftKind | LargeShipKind

/** Eye positions outside opaque cabin glazing, Y-up and +Z toward the bow. */
const FORWARD_VIEWS: Record<VesselKind, readonly [number, number, number]> = {
  tug: [0, 4.8, 4.8],
  icebreaker: [0, 14.4, 22],
  'cruise-ship': [0, 33, 89],
  'police-boat': [0, 2.5, 2.9],
  fireboat: [0, 2.7, 3],
  sailboat: [.65, 2.7, 3.7],
  zodiac: [0, 1.95, -.85],
  // The coxswain's post aft, looking over the cargo well at the closed ramp.
  'landing-craft': [0, 2.65, -3.6],
  'container-ship': [0, 46.2, 47.2],
  'aircraft-carrier': [-27, 41.2, -9.3],
}

// Fleet-wide cruising increase. With quadratic drag, dividing drag by the
// square of this value raises top speed by 25% while retaining low-speed
// thrust, towing pull and gentle initial acceleration.
const CRUISING_SPEED_SCALE = 1.25

// Base thrust/drag gives ~30 kn before the fleet-wide cruising increase.
// Plenty of bollard pull left on the wire.
const THRUST_AHEAD = 1_720_000
/** Astern thrust is weaker — a propeller is a worse pump run backward. */
const THRUST_ASTERN = 1_000_000
// Deliberately gentle for young players: a small azimuth angle so the helm
// eases the bow round over a few seconds instead of snapping it. Turning
// slowly is what makes towing a barge feel controllable rather than frantic.
const RUDDER_MAX = 0.24 // ~14 degrees

const PROPULSION = {
  ...SMALL_CRAFT_PROPULSION,
  ...LARGE_SHIP_PROPULSION,
  tug: { ahead: THRUST_AHEAD, astern: THRUST_ASTERN, rudder: RUDDER_MAX, lift: 4_000 },
  icebreaker: { ahead: 6_000_000, astern: 3_500_000, rudder: .38, lift: 200_000 },
  'cruise-ship': { ahead: 24_000_000, astern: 10_000_000, rudder: .4, lift: 2_000_000 },
} satisfies Record<VesselKind, { ahead: number; astern: number; rudder: number; lift: number }>

const _thrust = new Vector3()
const _point = new Vector3()

export class Tug extends Vessel {
  readonly group: Group
  readonly ready: Promise<void>
  readonly waterExclusion: HullWaterSource
  readonly forwardView: Vector3
  attachments: HullAttachments
  /** The landing craft's bow ramp; null for every other hull. */
  ramp: LandingRamp | null = null

  /** -1 (full astern) .. 1 (full ahead). */
  throttle = 0
  /** -1 (hard to port) .. 1 (hard to starboard). */
  rudder = 0

  constructor(readonly kind: VesselKind = 'tug') {
    super(isLargeShip(kind) ? { ...LARGE_SHIP_CONFIG[kind] } : isSmallCraft(kind) ? { ...SMALL_CRAFT_CONFIG[kind] } : kind === 'cruise-ship' ? {
      name: 'Giant Charlie of the Sea',
      mass: 24_000_000,
      length: 220.2,
      beam: 37.5,
      draft: 7.6,
      // About 28 kn after cruising tuning, with gradual acceleration.
      dragForward: 180_000,
      dragLateral: 2_500_000,
      dragYaw: 8e10,
      yawInertiaScale: 1.2,
      lateralResistanceOffset: 12,
      buoyancySamples: [12, 4],
      pitchDamping: 1,
      rollDamping: 1,
    } : kind === 'icebreaker' ? {
      name: 'Sea Cruise icebreaker',
      mass: 7_200_000,
      length: 96.2,
      beam: 23.5,
      draft: 6.7,
      // About 21 kn after cruising tuning, with a slower response than the tug.
      dragForward: 80_000,
      dragLateral: 900_000,
      dragYaw: 6e9,
      yawInertiaScale: 1.2,
      lateralResistanceOffset: 7,
      buoyancySamples: [8, 4],
      pitchDamping: 1,
      rollDamping: 1,
    } : {
      name: 'tug',
      mass: 280_000,
      length: 18,
      beam: 7,
      draft: 2.55,
      dragForward: 7_200, // near 37 kn after fleet-wide cruising tuning
      // High lateral drag makes the hull track straight and resist skidding
      // sideways, so it holds a course in tight water instead of feeling
      // skittish. Doesn't affect top speed (that's forward drag).
      dragLateral: 44_000,
      // Yaw damping and inertia raised hard to keep the helm calm — the tug
      // leans into a turn gradually and settles without darting, so a young
      // player isn't fighting a twitchy boat.
      dragYaw: 9.0e6,
      yawInertiaScale: 1.55,
      lateralResistanceOffset: 1.6,
      buoyancySamples: [4, 3],
    })

    this.config.dragForward /= CRUISING_SPEED_SCALE ** 2
    // Keep the faster Zodiac clear of the old 40 m/s numerical speed guard.
    this.config.maxSpeed = 40 * CRUISING_SPEED_SCALE

    this.group = new Group()
    this.forwardView = new Vector3(...FORWARD_VIEWS[kind])
    this.group.name = kind
    this.waterExclusion = { object: this.group, profile: HULL_WATER_PROFILES[kind] }
    if (kind !== 'tug') {
      this.attachments = {
        towPoint: new Vector3(),
        bridge: new Vector3(),
        exhaust: kind === 'cruise-ship' ? new Vector3(0, 43, -12) : new Vector3(0, 18, -7),
        propeller: new Vector3(),
      }
      const loadModel = isLargeShip(kind) ? () => loadLargeShipModel(kind) : isSmallCraft(kind) ? () => loadSmallCraftModel(kind) : kind === 'cruise-ship' ? loadCruiseShipModel : loadIcebreakerModel
      this.ready = loadModel().then(model => {
        // Resolve the authored nodes in the loader's +Z-forward frame.
        model.updateMatrixWorld(true)
        for (const [name, point] of [
          ['tow_point_stern', this.attachments.towPoint],
          ['camera_bridge', this.attachments.bridge],
          ['wake_stern', this.attachments.propeller],
        ] as const) {
          const node = model.getObjectByName(name)
          if (!node) throw new Error(`Missing vessel attachment: ${name}`)
          node.getWorldPosition(point)
        }
        if (isSmallCraft(kind) || isLargeShip(kind)) model.getObjectByName('exhaust')!.getWorldPosition(this.attachments.exhaust)
        const hinge = model.getObjectByName('ramp_hinge')
        if (hinge) this.ramp = new LandingRamp(hinge, model)
        this.group.add(model)
      })
    } else {
      this.attachments = {
        towPoint: new Vector3(0, 2.5, -6.4),
        bridge: new Vector3(0, 6.0, 2.2),
        exhaust: new Vector3(0, 6.6, -0.7),
        propeller: new Vector3(0, -1.4, -8.6),
      }
      this.ready = loadTugModel().then(model => { this.group.add(model) }).catch(error => {
        console.warn('Tug GLB failed to load, falling back to procedural hull:', error)
        this.waterExclusion.profile = HULL_WATER_PROFILES['tug-fallback']
        const fallback = buildTugHull()
        this.attachments = fallback.attachments
        this.group.add(fallback.group)
      })
    }
  }

  protected override applyControlForces(dt: number): void {
    void dt

    if (this.throttle !== 0) {
      const { ahead, astern, rudder } = PROPULSION[this.kind]
      // An open ramp is a barn door in the water: it bleeds away drive.
      const rampDrag = 1 - (this.ramp?.openFraction ?? 0) * .45
      const magnitude = (this.throttle > 0 ? ahead : astern) * this.throttle * rampDrag
      const angle = this.rudder * rudder

      // Thrust in the body frame, rotated by the azimuth angle. The sign puts
      // starboard helm (positive rudder) kicking the stern to port, which
      // swings the bow to screen-right under the chase camera — steering that
      // matches what your hands expect. Reduced grip when the hull is barely
      // in the water (riding over a crest).
      const grip = Math.min(1, this.submersion * 2)
      _thrust.set(Math.sin(angle) * magnitude * grip, 0, Math.cos(angle) * magnitude * grip)
      _point.set(0, 0, -this.config.length * 0.45)
      this.applyBodyForce(_thrust, _point)
    }

    // A touch of extra yaw authority underway, standing in for rudder lift.
    // Same sign convention as the azimuth thrust above; kept helm-true even
    // with sternway — realistic reversed backing is a cruelty to new sailors.
    const flow = Math.abs(this.forwardSpeed)
    this.applyYawTorque(-this.rudder * flow * flow * PROPULSION[this.kind].lift)
  }

  /** Sync the visual transform from the physics state. */
  syncVisual(): void {
    this.group.position.copy(this.position)
    this.group.quaternion.copy(this.quaternion)
  }

  /** World-space towing bollard position. */
  towPointWorld(target: Vector3): Vector3 {
    return this.localToWorld(this.attachments.towPoint, target)
  }

  /** Release GPU resources when switching vessels from the menu. */
  dispose(): void {
    const textures = new Set<Texture>()
    this.group.traverse(object => {
      if (!(object as Mesh).isMesh) return
      const mesh = object as Mesh
      mesh.geometry.dispose()
      const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
      for (const material of materials) {
        for (const value of Object.values(material)) if (value instanceof Texture) textures.add(value)
        material.dispose()
      }
    })
    for (const texture of textures) {
      texture.dispose()
      if (typeof ImageBitmap !== 'undefined' && texture.source.data instanceof ImageBitmap) texture.source.data.close()
    }
    this.group.clear()
  }
}
